import React, { useState } from 'react';
import { 
  Popover, 
  Box, 
  Tooltip 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ToolbarButton from './ToolbarButton';

const ColorGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(8, 1fr)',
  gap: 4,
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  minWidth: 260,
}));

const ColorButton = styled(Box)(({ theme, color, selected }) => ({
  width: 28,
  height: 28,
  backgroundColor: color,
  border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: 4,
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: theme.shadows[4],
  },
}));

const colors = [
  // Row 1 - Grays
  '#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#FFFFFF',
  // Row 2 - Reds
  '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#0000FF', '#9900FF', '#FF00FF',
  // Row 3 - Light variants
  '#F4CCCC', '#FCE5CD', '#FFF2CC', '#D9EAD3', '#D0E0E3', '#CFE2F3', '#D9D2E9', '#EAD1DC',
  // Row 4 - Medium variants
  '#EA9999', '#F9CB9C', '#FFE599', '#B6D7A8', '#A2C4C9', '#9FC5E8', '#B4A7D6', '#D5A6BD',
  // Row 5 - Dark variants
  '#CC0000', '#E69138', '#F1C232', '#6AA84F', '#45818E', '#3D85C6', '#674EA7', '#A64D79',
  // Row 6 - Darker variants
  '#990000', '#B45F06', '#BF9000', '#38761D', '#134F5C', '#0B5394', '#351C75', '#741B47',
];

const ColorPicker = ({ 
  icon, 
  tooltip, 
  value, 
  onChange, 
  type = 'color' // 'color' or 'background'
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorSelect = (color) => {
    onChange(type, color);
    handleClose();
  };

  const handleRemoveColor = () => {
    onChange(type, false);
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <ToolbarButton
          icon={icon}
          tooltip={tooltip}
          onClick={handleClick}
          active={Boolean(value)}
        />
        {value && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 16,
              height: 2,
              backgroundColor: value,
              border: '0.5px solid rgba(0,0,0,0.2)',
              borderRadius: 0.5,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box>
          <ColorGrid>
            {colors.map((color) => (
              <ColorButton
                key={color}
                color={color}
                selected={value === color}
                onClick={() => handleColorSelect(color)}
              />
            ))}
          </ColorGrid>
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
            <Box
              onClick={handleRemoveColor}
              sx={{
                textAlign: 'center',
                py: 0.5,
                cursor: 'pointer',
                fontSize: '0.875rem',
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                },
              }}
            >
              Remove {type === 'background' ? 'Background' : 'Color'}
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ColorPicker;